if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      appBlockObservers = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        this.addonsPlacementHandler = this.handleAddonsPlacement.bind(this);
        this.handleAddonsPlacement();
        document.addEventListener('matchMobile', this.addonsPlacementHandler);
        document.addEventListener('unmatchMobile', this.addonsPlacementHandler);

        this.mobileInventoryPlacementHandler = this.handleMobileInventoryPlacement.bind(this);
        this.handleMobileInventoryPlacement();
        document.addEventListener('matchMobile', this.mobileInventoryPlacementHandler);
        document.addEventListener('unmatchMobile', this.mobileInventoryPlacementHandler);

        this.initAppBlockObservers();

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);

          this.mobileAboveMediaHandler = this.handleMobileAboveMediaBlocks.bind(this);
          this.handleMobileAboveMediaBlocks();
          document.addEventListener('matchMobile', this.mobileAboveMediaHandler);
          document.addEventListener('unmatchMobile', this.mobileAboveMediaHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      handleMobileAboveMediaBlocks() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-mobile-above-media="true"]`);
        const container = this.productMediaWrapper?.querySelector('[data-mobile-above-media-container]');

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper?.querySelector(`[data-mobile-above-media-block="${blockId}"]`);
          if (!blockPlacement) return;

          if (!block.mobileAboveMediaAnchor) {
            block.mobileAboveMediaAnchor = document.createComment(`mobile-above-media:${blockId}`);
            block.before(block.mobileAboveMediaAnchor);
          }

          if (isMobile) {
            if (block.parentElement !== blockPlacement) {
              blockPlacement.appendChild(block);
            }
          } else if (block.mobileAboveMediaAnchor.parentNode) {
            block.mobileAboveMediaAnchor.after(block);
          }
        });

        if (container) {
          container.hidden = !isMobile || !container.querySelector('.product__block');
        }
      }

      handleAddonsPlacement() {
        const sourceBlock = this.querySelector('[data-addons-source-block]');
        const desktopTarget = this.querySelector('[data-addons-target="desktop"]');
        const mobileTarget = this.querySelector('[data-addons-target="mobile"]');

        if (!sourceBlock) return;

        const addonsContent =
          sourceBlock.querySelector('[data-addons-content]') ||
          desktopTarget?.querySelector('[data-addons-content]') ||
          mobileTarget?.querySelector('[data-addons-content]');

        if (!addonsContent) return;

        const mobileInventoryBlock = mobileTarget?.closest('[id^="Inventory-"]');
        const mobileInventoryContent = mobileInventoryBlock?.querySelector('[data-inventory-content]');
        const hasVisibleInventory =
          mobileTarget &&
          mobileInventoryBlock &&
          !mobileInventoryBlock.classList.contains('hidden') &&
          Boolean(mobileInventoryContent?.textContent?.trim());

        const desktopMetaBlock = desktopTarget?.closest('.product__block--meta');
        const hasVisibleMeta = desktopTarget && Boolean(desktopMetaBlock?.querySelector('.product__meta:not(.hidden)'));

        let target = sourceBlock;
        if (FoxTheme.config.mqlMobile && hasVisibleInventory) {
          target = mobileTarget;
        } else if (!FoxTheme.config.mqlMobile && hasVisibleMeta) {
          target = desktopTarget;
        }

        if (addonsContent.parentElement !== target) {
          target.appendChild(addonsContent);
        }

        sourceBlock.hidden = target !== sourceBlock;
      }

      handleMobileInventoryPlacement() {
        const blocksContainer = this.querySelector('.product__info-container .product__blocks');
        if (!blocksContainer) return;

        const blocks = Array.from(blocksContainer.children);
        const priceBlock = blocks.find((block) => block.classList.contains('product__block--price'));
        const inventoryBlock = blocks.find((block) => block.classList.contains('product__block--inventory'));

        if (!priceBlock || !inventoryBlock) return;

        if (!inventoryBlock.mobileInventoryAnchor) {
          inventoryBlock.mobileInventoryAnchor = document.createComment('mobile-inventory-anchor');
          inventoryBlock.before(inventoryBlock.mobileInventoryAnchor);
        }

        if (FoxTheme.config.mqlMobile) {
          if (inventoryBlock.nextElementSibling !== priceBlock) {
            priceBlock.before(inventoryBlock);
          }
        } else if (inventoryBlock.mobileInventoryAnchor.parentNode) {
          inventoryBlock.mobileInventoryAnchor.after(inventoryBlock);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();

        if (this.addonsPlacementHandler) {
          document.removeEventListener('matchMobile', this.addonsPlacementHandler);
          document.removeEventListener('unmatchMobile', this.addonsPlacementHandler);
        }

        if (this.mobileInventoryPlacementHandler) {
          document.removeEventListener('matchMobile', this.mobileInventoryPlacementHandler);
          document.removeEventListener('unmatchMobile', this.mobileInventoryPlacementHandler);
        }

        if (this.blockPositionHandler) {
          document.removeEventListener('matchMobile', this.blockPositionHandler);
          document.removeEventListener('unmatchMobile', this.blockPositionHandler);
        }

        if (this.mobileAboveMediaHandler) {
          document.removeEventListener('matchMobile', this.mobileAboveMediaHandler);
          document.removeEventListener('unmatchMobile', this.mobileAboveMediaHandler);
        }

        this.disconnectAppBlockObservers();
      }

      initializeProductSwapUtility() {
        if (this.productSwapUtilityInitialized) return;
        this.productSwapUtilityInitialized = true;

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });

        this.postProcessHtmlCallbacks.push(() => {
          this._productMediaWrapper = null;
          this.handleMobileInventoryPlacement();
          if (this.querySelector('.product__media-wrapper')) {
            this.handleBlocksPosition();
            this.handleMobileAboveMediaBlocks();
          }
          this.handleAddonsPlacement();
          this.initAppBlockObservers();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              if (id === 'Inventory') {
                const sourceContent = source.querySelector('[data-inventory-content]');
                const destinationContent = destination.querySelector('[data-inventory-content]');
                const sourceProgress = source.querySelector('[data-inventory-progress]');
                const destinationProgress = destination.querySelector('[data-inventory-progress]');

                if (sourceContent && destinationContent) {
                  destinationContent.innerHTML = sourceContent.innerHTML;

                  if (sourceProgress && destinationProgress) {
                    destinationProgress.innerHTML = sourceProgress.innerHTML;
                  }
                } else {
                  destination.innerHTML = source.innerHTML;
                }
              } else {
                destination.innerHTML = source.innerHTML;
              }
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination(
            'Inventory',
            (source) => source.querySelector('[data-inventory-content]')?.textContent?.trim() === '',
          );
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('BadgesOverlay', (source) => source.innerHTML.trim() === '');
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          const getAddonContent = (root) =>
            root.querySelector('[data-addons-content]') || root.querySelector('.product__block--addons .product__addons');
          const sourceAddons = getAddonContent(html);
          const destinationAddons = getAddonContent(this);

          if (sourceAddons && destinationAddons) {
            const sourceAddonItems = sourceAddons.querySelectorAll('.product__addons-item');
            const destinationAddonItems = destinationAddons.querySelectorAll('.product__addons-item');

            if (sourceAddonItems.length === destinationAddonItems.length) {
              destinationAddonItems.forEach((item, index) => {
                item.innerHTML = sourceAddonItems[index].innerHTML;
              });
            }
          }

          this.querySelectorAll('wishlist-button-block[data-variant-id], .wkh-button[data-variant-id]').forEach((el) => {
            el.setAttribute('data-variant-id', variant.id);
          });

          this.handleAddonsPlacement();
          this.initAppBlockObservers();

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      initAppBlockObservers(root = this) {
        this.disconnectAppBlockObservers();

        const appBlocks = root.querySelectorAll('[data-product-app-block]');
        appBlocks.forEach((block) => {
          const updateAppBlockVisibility = () => {
            const wasEmpty = block.classList.contains('product__block--empty-app');
            if (wasEmpty) block.classList.remove('product__block--empty-app');

            const isEmpty = !this.hasVisibleAppBlockContent(block);
            block.classList.toggle('product__block--empty-app', isEmpty);
          };

          updateAppBlockVisibility();
          requestAnimationFrame(updateAppBlockVisibility);
          setTimeout(updateAppBlockVisibility, 500);
          setTimeout(updateAppBlockVisibility, 1500);

          if (!window.MutationObserver) return;

          const observer = new MutationObserver((records) => {
            const onlyEmptyStateChanged = records.every(
              (record) => record.type === 'attributes' && record.target === block && record.attributeName === 'class',
            );
            if (onlyEmptyStateChanged) return;

            updateAppBlockVisibility();
          });
          observer.observe(block, {
            attributes: true,
            attributeFilter: ['class', 'hidden', 'style'],
            childList: true,
            subtree: true,
          });
          this.appBlockObservers.push(observer);
        });
      }

      disconnectAppBlockObservers() {
        this.appBlockObservers.forEach((observer) => observer.disconnect());
        this.appBlockObservers = [];
      }

      hasVisibleAppBlockContent(block) {
        const appRoot = block.querySelector('.shopify-app-block') || block;
        const isVisibleContent = (element) => {
          if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE' || element.tagName === 'TEMPLATE') {
            return false;
          }

          if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
            return false;
          }

          const style = window.getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
            return false;
          }

          const hasVisibleBox = Array.from(element.getClientRects()).some((rect) => rect.width > 0 && rect.height > 0);
          if (!hasVisibleBox) return false;

          if (element.matches('button, a, input, select, textarea, img, svg, video, canvas, iframe, [role], [tabindex]')) {
            return true;
          }

          const hasDirectText = Array.from(element.childNodes).some(
            (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '',
          );
          if (hasDirectText) return true;

          return Array.from(element.children).some(isVisibleContent);
        };

        return Array.from(appRoot.children).some(isVisibleContent);
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}
